import ApiService from "../../services/ApiService";
import { resolveApiFetchUrl } from "../../services/ApiBaseUrl";
import { apiFetch } from "../../services/apiFetch";

async function optionalAuthHeaders() {
    const headers = new Headers();
    headers.set("Content-Type", "application/json");
    try {
        await ApiService.getAccessToken();
        const bearer = ApiService.init.headers.get("Authorization");
        if (bearer) {
            headers.set("Authorization", bearer);
        }
    } catch {
        /* guest */
    }
    return headers;
}

/**
 * Latest Facebook-posted Word Wheel guess-word for the public homepage.
 * @returns {Promise<object|null>}
 */
export async function fetchLatestPostedWordWheelSocial() {
    const headers = await optionalAuthHeaders();
    const result = await apiFetch(
        resolveApiFetchUrl("/v1/puzzle/wordwheel-social/latest-posted"),
        { method: "GET", headers },
    );
    if (result.status === 204) {
        return null;
    }
    const json = await result.json().catch(() => null);
    if (!result.ok) {
        if (json?.code === "NO_DATA") return null;
        const error = new Error(json?.message || `HTTP ${result.status}`);
        error.status = result.status;
        throw error;
    }
    if (!json || json.code === "NO_DATA") {
        return null;
    }
    return json;
}

/** Parse stored caption blanks (G _ O D) so the homepage matches Facebook. */
export function slotsFromMaskedCaption(masked, word) {
    const w = String(word || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "");
    const compact = String(masked || "")
        .toUpperCase()
        .replace(/\s+/g, "");
    if (!w) return [];
    if (compact.length !== w.length) return [];
    return w.split("").map((_, i) => {
        const m = compact[i];
        return m === "_" || m === "-" || m === "·" || m === "•" ? "" : w[i];
    });
}

/**
 * GOOD → slots. Uses stored Facebook blanks when provided; otherwise a stable
 * varied pattern (not always first/last).
 */
export function maskWordSlots(word, maskedCaption) {
    const w = String(word || "")
        .toUpperCase()
        .replace(/[^A-Z]/g, "");
    if (maskedCaption) {
        const fromStored = slotsFromMaskedCaption(maskedCaption, w);
        if (fromStored.length === w.length) return fromStored;
    }
    if (w.length === 0) return [];
    if (w.length <= 2) return w.split("");
    const vowels = new Set(["A", "E", "I", "O", "U"]);
    const n = w.length;
    const options = [];
    const push = (reveal) => {
        const shown = reveal.filter(Boolean).length;
        const blank = n - shown;
        if (shown < 1 || blank < 1) return;
        const key = reveal.map((b) => (b ? "1" : "0")).join("");
        if (options.some((r) => r.map((b) => (b ? "1" : "0")).join("") === key)) return;
        options.push(reveal);
    };
    const at = (...idx) => {
        const reveal = Array(n).fill(false);
        idx.forEach((i) => {
            if (i >= 0 && i < n) reveal[i] = true;
        });
        push(reveal);
    };
    at(0);
    at(n - 1);
    at(0, n - 1);
    push(Array.from({ length: n }, (_, i) => i < 2));
    push(Array.from({ length: n }, (_, i) => i >= n - 2));
    if (n >= 4) {
        at(0, n - 2, n - 1);
        at(0, 1, n - 1);
    }
    if (n >= 5) {
        push(Array.from({ length: n }, (_, i) => i < 3));
        push(Array.from({ length: n }, (_, i) => i >= n - 3));
        at(0, Math.floor(n / 2), n - 1);
    }
    push(w.split("").map((ch) => vowels.has(ch)));
    push(w.split("").map((ch) => !vowels.has(ch)));
    push(Array.from({ length: n }, (_, i) => i % 2 === 0));
    push(Array.from({ length: n }, (_, i) => i % 2 === 1));
    const use = options.length > 0 ? options : [Array.from({ length: n }, (_, i) => i === 0 || i === n - 1)];
    let sum = 0;
    for (let i = 0; i < w.length; i += 1) {
        sum += w.charCodeAt(i);
    }
    const reveal = use[sum % use.length];
    return w.split("").map((ch, i) => (reveal[i] ? ch : ""));
}

const CARD_GRADIENTS = [
    { from: "#c2410c", to: "#fb923c", angle: 140 },
    { from: "#1e3a8a", to: "#38bdf8", angle: 145 },
    { from: "#14532d", to: "#4ade80", angle: 135 },
    { from: "#115e59", to: "#2dd4bf", angle: 138 },
    { from: "#4c1d95", to: "#c084fc", angle: 148 },
    { from: "#991b1b", to: "#f87171", angle: 132 },
    { from: "#a16207", to: "#facc15", angle: 128 },
    { from: "#0f172a", to: "#3b82f6", angle: 150 },
];

/** Palette used on Facebook guess-the-word cards (email uses solid `from` as fallback). */
export function cardPaletteForWord(word) {
    const w = String(word || "");
    let sum = 0;
    for (let i = 0; i < w.length; i += 1) {
        sum += w.charCodeAt(i);
    }
    return CARD_GRADIENTS[sum % CARD_GRADIENTS.length];
}

/** Stable warm gradient matching the Facebook guess-the-word cards. */
export function gradientCssForWord(word) {
    const set = cardPaletteForWord(word);
    return `linear-gradient(${set.angle}deg, ${set.from} 0%, ${set.to} 100%)`;
}

/** Shown when nothing has been posted to Facebook yet. */
export const DEMO_GUESS_POST = {
    word: "GOOD",
    maskedWord: "_ O O _",
    meaning: "Kind, pleasant, or of high quality",
};
